/*
 * fs-family-shop.js — the silhouette picker, and the buy bar that follows it.
 *
 * WHAT IT DOES, AND WHAT IT DELIBERATELY DOES NOT
 * Everything on the page is already rendered by the store: seven panels of
 * products and seven rows of the sticky bar, one per family. This file only
 * decides which of them is visible. It never builds markup, never fetches, and
 * never holds a price or a variant id — so the bar cannot drift out of step
 * with the panel, because they are the same server-rendered pair.
 *
 * ⚠️ THE ONE RULE THIS THEME KEEPS RELEARNING
 * Setting `hidden` is not the same as being hidden. A previous section had two
 * groups both carrying `hidden` and both painting, because a display rule
 * elsewhere out-specified the browser default. The CSS here declares
 * `[hidden]{display:none !important}` against these elements' own classes.
 * Verify on the SCREEN, not on the attribute.
 *
 * WITHOUT THIS FILE the first family's panel and bar are shown, every product
 * is a real link and every buy button is a real form post. Nothing becomes
 * unreachable — the page simply stops switching.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-fs-fam]');
  if (!root) return;

  var bar = document.querySelector('[data-fs-fambar]');
  var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-fs-fam-tab]'));
  var panels = Array.prototype.slice.call(root.querySelectorAll('[data-fs-fam-panel]'));
  var rows = bar ? Array.prototype.slice.call(bar.querySelectorAll('[data-fs-fambar-row]')) : [];
  if (!tabs.length) return;

  var dismissed = false;

  function select(id, moveFocus) {
    tabs.forEach(function (t) {
      t.setAttribute('aria-selected', String(t.getAttribute('data-fs-fam-tab') === id));
    });
    panels.forEach(function (p) {
      if (p.getAttribute('data-fs-fam-panel') === id) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
    rows.forEach(function (r) {
      if (r.getAttribute('data-fs-fambar-row') === id) r.removeAttribute('hidden');
      else r.setAttribute('hidden', '');
    });

    /* A rail left mid-scroll from a previous visit reopens showing card three.
       Reset it so every family opens on its first product. */
    var rail = root.querySelector('[data-fs-fam-panel="' + id + '"] [data-fs-rail]');
    if (rail) rail.scrollLeft = 0;

    if (moveFocus) {
      var active = tabs.filter(function (t) { return t.getAttribute('data-fs-fam-tab') === id; })[0];
      if (active) active.focus();
    }
  }

  root.addEventListener('click', function (e) {
    var tab = e.target.closest && e.target.closest('[data-fs-fam-tab]');
    if (!tab) return;
    select(tab.getAttribute('data-fs-fam-tab'), false);
  });

  /* Arrow keys move between tabs, which is what a tablist is expected to do and
     what a keyboard user will try first. Home and End jump to the ends. */
  root.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-fs-fam-tab]');
    if (!tab) return;
    var i = tabs.indexOf(tab);
    var next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    select(tabs[next].getAttribute('data-fs-fam-tab'), true);
  });

  /* ------------------------------------------------------------ the chips
   *
   * A card offering more than one buyable variant lets the shopper pick before
   * the button does. The chips carry the option position and the value; the
   * card carries the buyable variants as JSON. Nothing here knows a price or a
   * product — it reads what the store rendered.
   *
   * WHY IT SYNCS THE OTHER CHIPS INSTEAD OF DISABLING THEM
   * Options multiply: picking "4-in-1" and then "Clear" may name a combination
   * that is sold out. Disabling every impossible chip means a shopper's second
   * click often lands on something greyed out, and the row reads as broken. So
   * the pressed chip always wins: the first buyable variant matching it is
   * selected, and every other row moves to match. The shopper always ends on
   * something real, and can never reach a dead end.
   *
   * With this file absent, the form still holds the first buyable variant and
   * the button still adds it. The chips simply stop responding.
   */
  function cardVariants(card) {
    var el = card.querySelector('[data-fs-card-variants]');
    if (!el) return null;
    try { return JSON.parse(el.textContent).variants; } catch (e) { return null; }
  }

  function paintCard(card, variant) {
    var idField = card.querySelector('[data-fs-card-id]');
    if (idField) idField.value = variant.id;

    var price = card.querySelector('[data-fs-card-price]');
    if (price) price.textContent = variant.price;

    /* Only swap the picture when the chosen variant has one of its own —
       otherwise a colour without its own photograph would blank the card. */
    if (variant.image) {
      var img = card.querySelector('.fs-fam__cardmedia img');
      if (img) { img.removeAttribute('srcset'); img.src = variant.image; }
    }

    card.querySelectorAll('[data-fs-chip]').forEach(function (chip) {
      var pos = Number(chip.getAttribute('data-fs-chip'));
      var on = variant.options[pos] === chip.getAttribute('data-fs-chip-value');
      chip.setAttribute('aria-pressed', String(on));
    });
  }

  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('[data-fs-chip]');
    if (!chip) return;
    var card = chip.closest('[data-fs-card]');
    if (!card) return;
    var variants = cardVariants(card);
    if (!variants || !variants.length) return;

    var pos = Number(chip.getAttribute('data-fs-chip'));
    var want = chip.getAttribute('data-fs-chip-value');

    /* What every other row currently reads. */
    var current = [];
    card.querySelectorAll('[data-fs-chip][aria-pressed="true"]').forEach(function (c) {
      current[Number(c.getAttribute('data-fs-chip'))] = c.getAttribute('data-fs-chip-value');
    });
    current[pos] = want;

    /* Best match first — every row agreeing. Then anything at all that carries
       the value just pressed, which is the guarantee against a dead end. */
    var exact = variants.filter(function (v) {
      return current.every(function (val, i) { return val == null || v.options[i] === val; });
    })[0];
    var loose = variants.filter(function (v) { return v.options[pos] === want; })[0];

    var pick = exact || loose;
    if (pick) paintCard(card, pick);
  });

  if (!bar) return;

  if (bar.querySelector('[data-fs-fambar-close]') !== null) {
    bar.addEventListener('click', function (e) {
      if (!e.target.closest('[data-fs-fambar-close]')) return;
      dismissed = true;
      bar.classList.remove('is-on');
      /* Wait for the slide-out before taking it out of the layout, or it
         vanishes instead of leaving. */
      window.setTimeout(function () { if (dismissed) bar.setAttribute('hidden', ''); }, 300);
    });
  }

  /*
   * WHEN THE BAR APPEARS
   * As soon as the shopper has scrolled past the silhouettes — so it is there
   * for the whole of the slider and everything below it, which is the point of
   * it. It hides again above that line, because a bar selling "the family you
   * picked" is nonsense before anybody has picked one.
   *
   * ⚠️ IT WATCHES THE PICKER ROW, NOT THE SECTION. Watching the section only
   * fires once the WHOLE band has left the screen — so the bar would appear
   * after the slider rather than beside it, which is backwards. Measured, not
   * assumed: the band is roughly two screens tall.
   *
   * `boundingClientRect.top < 0` is the other load-bearing half. Without it the
   * bar fires on first paint whenever the section starts below the fold, which
   * is every phone.
   */
  if (!('IntersectionObserver' in window)) return;

  function show(on) {
    if (dismissed) return;
    if (on) {
      bar.removeAttribute('hidden');
      /* One frame between un-hiding and the class, or the browser has nothing
         to animate from and the bar simply appears. */
      window.requestAnimationFrame(function () { bar.classList.add('is-on'); });
    } else {
      bar.classList.remove('is-on');
    }
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      show(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
  }, { threshold: 0 });

  var anchor = root.querySelector('[data-fs-fam-anchor]') || root;
  io.observe(anchor);
})();
